import React, { useEffect, useMemo, useState } from "react";
import { FaBoxOpen, FaExclamationTriangle } from "react-icons/fa";
import axios from "axios";
import { baseurl } from "../../config/url";
import Loader from "../Loader";

/**
 * ✅ LowStockTable — UI/UX Upgrade (logic same)
 * - Professional header + summary chips
 * - Search + filter (All / Low / Critical)
 * - Empty state + better spacing + consistent typography
 * - Sticky header inside scroll container
 */

const LowStockTable = () => {
  const [items, setItems] = useState([]);
  const [stockStatus, setStockStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("all"); // all | low | critical

  useEffect(() => {
    const sellerId = localStorage.getItem("sellerId");
    const requestedStock = 500;

    const fetch = async () => {
      try {
        setLoading(true);
        setError(null);

        const res = await axios.get(
          `${baseurl}/api/seller/check-stock/${sellerId}/${requestedStock}`
        );

        const data = res.data;

        if (data?.message === "Stock is sufficient") {
          setStockStatus("All products have sufficient stock.");
          setItems([]);
        } else {
          setStockStatus("");
          setItems(Array.isArray(data?.products) ? data.products : []);
        }
      } catch (err) {
        console.error(err);
        setError("Failed to fetch products.");
      } finally {
        setLoading(false);
      }
    };

    fetch();
  }, []);

  const CRITICAL = 300;
  const LOW = 500;

  const stats = useMemo(() => {
    const total = items.length;
    const critical = items.filter((p) => Number(p?.stock ?? 0) <= CRITICAL).length;
    const low = items.filter(
      (p) => Number(p?.stock ?? 0) > CRITICAL && Number(p?.stock ?? 0) < LOW
    ).length;
    return { total, low, critical };
  }, [items]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();

    return items
      .filter((p) => {
        const name = String(p?.name ?? "").toLowerCase();
        const matchQuery = !q || name.includes(q);

        const stock = Number(p?.stock ?? 0);
        const isCritical = stock <= CRITICAL;
        const isLow = stock > CRITICAL && stock < LOW;

        const matchFilter =
          filter === "all" ? true : filter === "critical" ? isCritical : isLow;

        return matchQuery && matchFilter;
      })
      .sort((a, b) => Number(a?.stock ?? 0) - Number(b?.stock ?? 0));
  }, [items, query, filter]);

  const getBadge = (stock) => {
    const s = Number(stock ?? 0);
    if (s <= CRITICAL) {
      return {
        label: "Critical",
        wrap: "bg-red-50 text-red-700 border-red-200",
        icon: <FaExclamationTriangle className="text-yellow-500" />,
      };
    }
    if (s < LOW) {
      return {
        label: "Low",
        wrap: "bg-orange-50 text-orange-700 border-orange-200",
        icon: <FaExclamationTriangle className="text-yellow-500" />,
      };
    }
    return {
      label: "In stock",
      wrap: "bg-green-50 text-green-700 border-green-200",
      icon: null,
    };
  };

  if (loading) {
    return (
      <div className="h-[420px] bg-gray-50 p-6 rounded-xl shadow-lg mb-10 flex justify-center items-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="bg-gray-50 shadow-xl rounded-xl overflow-hidden">
      {/* Sticky header */}
      <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200">
        <div className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Seller Products Stock
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                Monitor your uploaded products and identify low/critical stock.
              </p>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-gray-200 bg-white text-gray-700">
                Total: {stats.total}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-orange-200 bg-orange-50 text-orange-700">
                Low: {stats.low}
              </span>
              <span className="px-3 py-1 rounded-full text-xs font-semibold border border-red-200 bg-red-50 text-red-700">
                Critical: {stats.critical}
              </span>
            </div>
          </div>

          {/* Controls */}
          <div className="mt-5 flex flex-col md:flex-row gap-3 md:items-center md:justify-between">
            <div className="flex-1">
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search product name..."
                className="w-full bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-200"
              />
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setFilter("all")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                  filter === "all"
                    ? "bg-blue-600 text-white border-blue-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                }`}
              >
                All
              </button>
              <button
                onClick={() => setFilter("low")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                  filter === "low"
                    ? "bg-orange-600 text-white border-orange-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                }`}
              >
                Low
              </button>
              <button
                onClick={() => setFilter("critical")}
                className={`px-4 py-2 rounded-lg text-sm font-semibold border transition ${
                  filter === "critical"
                    ? "bg-red-600 text-white border-red-600"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-100"
                }`}
              >
                Critical
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mt-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {stockStatus && !error && (
            <div className="mt-4 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
              {stockStatus}
            </div>
          )}
        </div>
      </div>

      {/* List */}
      <div className="max-h-[420px] overflow-y-auto">
        {!error && !stockStatus && filteredItems.length === 0 ? (
          <div className="p-10 text-center">
            <div className="mx-auto w-12 h-12 rounded-full bg-white border border-gray-200 flex items-center justify-center text-blue-600">
              <FaBoxOpen />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900">
              No products found
            </h3>
            <p className="mt-1 text-sm text-gray-600">
              Try clearing search or switching the filter.
            </p>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            {filteredItems.map((item) => {
              const badge = getBadge(item?.stock);
              const stockNum = Number(item?.stock ?? 0);

              return (
                <div
                  key={item?._id}
                  className="group bg-white border border-gray-200 rounded-xl px-4 py-4 shadow-sm hover:shadow-md transition"
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="min-w-0 flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gray-50 border border-gray-200 flex items-center justify-center text-blue-600">
                        <FaBoxOpen />
                      </div>

                      <div className="min-w-0">
                        <div className="truncate text-sm md:text-base font-semibold text-gray-900">
                          {item?.name || "Unnamed Product"}
                        </div>
                        <div className="mt-1 flex items-center gap-2">
                          <span
                            className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full text-xs font-semibold border ${badge.wrap}`}
                          >
                            {badge.icon}
                            {badge.label}
                          </span>
                          <span className="text-xs text-gray-500">
                            Stock threshold: {LOW}
                          </span>
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-1">
                      <div className="text-sm text-gray-600">Available</div>
                      <div
                        className={`text-lg font-bold ${
                          stockNum <= CRITICAL
                            ? "text-red-700"
                            : stockNum < LOW
                            ? "text-orange-700"
                            : "text-green-700"
                        }`}
                      >
                        {stockNum}
                      </div>
                    </div>
                  </div>

                  {/* Small progress bar */}
                  <div className="mt-3">
                    <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                      <div
                        className={`h-full rounded-full ${
                          stockNum <= CRITICAL
                            ? "bg-red-500"
                            : stockNum < LOW
                            ? "bg-orange-500"
                            : "bg-green-500"
                        }`}
                        style={{
                          width: `${Math.min(100, Math.round((stockNum / LOW) * 100))}%`,
                        }}
                      />
                    </div>
                    <div className="mt-1 flex justify-between text-xs text-gray-500">
                      <span>0</span>
                      <span>{LOW}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LowStockTable;